import React, { useContext, useEffect, useState } from "react"
import { Div, Col, Row, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import SecondaryButton from "../buttons/SecondaryButton"
import PrimaryButton from "../buttons/PrimaryButton"
import Container from "../atoms/Container"
import Switch from "../atoms/Switch"
import {
	faChevronDown,
	faChevronLeft,
	faChevronRight,
	faChevronUp,
	faClock,
	faKey,
	faSliders,
	faUserMinus,
	faUserPlus,
} from "@fortawesome/free-solid-svg-icons"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { toast } from "react-toastify"
import ToggleChip from "../atoms/ToggleChip"
import { callFunction } from "../services/firebase.service"
import { subscribeToSchoolCoaches } from "../utils/firestore.utils"
import { accessOf, ownsSchool, ROLE_LABELS, ROLE_PRESETS, roleOf } from "../utils/school.utils"
import { teachesInSchool } from "../utils/func.utils"
import HelpTip from "../help/HelpTip"
import CoachAvatar from "../atoms/CoachAvatar"
import {
	Block,
	Divider,
	RadioBlock,
	RadioRow,
	ROLE_HINTS,
	RowButton,
	ScrollArea,
	StepTransition,
	WideButton,
} from "../school/SheetBlocks"

const LESSON_LEVELS = [
	{ level: "none", text: "No" },
	{ level: "view", text: "Vede" },
	{ level: "edit", text: "Modifica" },
]

// Chi c'è nel circolo diviso per quello che può fare: prima chi lo gestisce,
// poi chi guarda tutto, poi i colleghi. Chi ha un ruolo personalizzato sta
// coi colleghi: ha qualcosa in più, ma non il circolo intero.
const GROUPS = [
	{
		roles: ["owner", "manager"],
		title: "Gestiscono il circolo",
		hint: "modificano le lezioni di tutti, fanno entrare colleghi",
	},
	{
		roles: ["viewer"],
		title: "Vedono tutto",
		hint: "giornata e riepilogo di tutti i maestri, senza toccare",
	},
	{
		roles: ["colleague", "custom"],
		title: "Colleghi",
		hint: "le loro lezioni, e quelle che gli affidi",
	},
]

// Cosa resta chi passa il circolo: gestore, o collega
const OWNER_BECOMES = ["manager", "colleague"].map(value => ({
	value,
	title: ROLE_LABELS[value],
	hint:
		value === "manager"
			? "Modifichi le lezioni di tutti, fai entrare colleghi, cambi campi e listino"
			: "Le tue lezioni, e quelle che ti affidano",
}))

const firstNameOf = name => (name ?? "").split(" ")[0]

// Chi c'è nel circolo, e cosa può fare ognuno. Sta dietro al tassello
// "Maestri" del circolo.
//
// L'elenco dice solo chi c'è, diviso per ruolo; tutto il resto sta nella
// scheda della persona, che si apre toccando il nome: se insegna qui, che
// ruolo ha, Personalizza, toglierlo dal circolo. Prima ogni riga aveva tutto
// insieme — ruoli, permessi, descrizioni — e più cose si potevano scegliere
// più l'elenco diventava difficile da leggere.
//
// Chi fa cosa lo decide solo il proprietario, compreso chi insegna e chi no
// (anche per sé: un proprietario che non insegna resta proprietario). Gli
// altri leggono soltanto.
export function SchoolTeamModal({ closeModal }) {
	const Modal = useContext(modalContext)
	const { coach } = useContext(profileContext)
	const { getSelectedSchool, selectedSchool, canInviteTo } = useContext(schoolContext)

	const [coaches, setCoaches] = useState([])
	const [changingID, setChangingID] = useState(null)
	const [openID, setOpenID] = useState(null)
	// da che parte entra la fase nuova: 1 avanti, -1 indietro, 0 all'apertura
	const [direction, setDirection] = useState(0)
	const openPerson = coachID => {
		setDirection(coachID ? 1 : -1)
		setOpenID(coachID)
	}
	const [isCustomizing, setIsCustomizing] = useState(false)
	// a chi si sta per passare il circolo, e cosa si resta
	const [passingTo, setPassingTo] = useState(null)
	const [ownerBecomes, setOwnerBecomes] = useState("manager")
	const startPassing = coachID => {
		setDirection(coachID ? 1 : -1)
		setPassingTo(coachID)
	}

	useEffect(() => subscribeToSchoolCoaches(selectedSchool, setCoaches), [selectedSchool])

	const school = getSelectedSchool()
	if (!school) return null

	const isOwner = ownsSchool(coach, school)
	const canInvite = canInviteTo(selectedSchool)

	const nameOf = coachID =>
		coaches.find(colleague => colleague.coachID === coachID)?.coachName ?? coachID

	// Solo i colleghi che ci sono ancora: un nome rimasto a qualcuno che è
	// uscito non dice niente a nessuno, e il server lo toglie al primo salvataggio.
	const accessHere = colleague => {
		const access = accessOf(colleague, school, selectedSchool)
		const coachesHere = Object.fromEntries(
			Object.entries(access.coaches).filter(([coachID]) =>
				coaches.some(other => other.coachID === coachID),
			),
		)
		return { ...access, coaches: coachesHere }
	}

	// Cosa può fare, detto in poche righe: è quello che legge chi non è il
	// proprietario, e il riassunto di un ruolo personalizzato.
	const describe = colleague => {
		const role = roleOf(colleague, school, selectedSchool)
		if (role === "owner") return ["Può tutto, e decide cosa fanno gli altri"]

		const access = accessHere(colleague)
		const lines = []
		const named = level =>
			Object.entries(access.coaches)
				.filter(([, value]) => value === level)
				.map(([coachID]) => nameOf(coachID))

		if (access.allLessons === "edit")
			lines.push("Modifica e assegna le lezioni di tutti i maestri")
		if (access.allLessons === "view") lines.push("Vede giornata e riepilogo di tutti i maestri")
		if (named("edit").length > 0)
			lines.push(`Modifica e assegna le lezioni di ${named("edit").join(", ")}`)
		if (named("view").length > 0)
			lines.push(`Vede giornata e riepilogo di ${named("view").join(", ")}`)
		if (access.invite) lines.push("Fa entrare colleghi")
		if (access.editSchool) lines.push("Cambia campi, listino e dati del circolo")
		if (lines.length === 0) lines.push("Le sue lezioni, e quelle che gli affidi")
		return lines
	}

	const save = async (colleague, access, message) => {
		setChangingID(colleague.coachID)
		try {
			const setColleagueAccess = callFunction("setColleagueAccess")
			await setColleagueAccess({
				schoolID: selectedSchool,
				coachID: colleague.coachID,
				access: {
					allLessons: access.allLessons,
					coaches: Object.fromEntries(
						Object.entries(access.coaches).filter(([, level]) => level !== "none"),
					),
					invite: access.invite,
					editSchool: access.editSchool,
				},
			})
			toast.success(message, { position: "top-center" })
		} catch (err) {
			toast.error(err.message || "Non sono riuscito a cambiarlo", { position: "top-center" })
		} finally {
			setChangingID(null)
		}
	}

	const name = colleague => colleague.coachName ?? colleague.coachID
	const isMe = colleague => colleague.coachID === coach?.coachID

	// Insegna qui o no: il circolo passa da `teachesIn` a `notTeachingIn` e
	// ritorno, e il ruolo resta com'è
	const setTeaching = async (colleague, teaches) => {
		setChangingID(colleague.coachID)
		try {
			const setColleagueTeaching = callFunction("setColleagueTeaching")
			await setColleagueTeaching({
				schoolID: selectedSchool,
				coachID: colleague.coachID,
				teaches,
			})
			toast.success(
				isMe(colleague)
					? teaches
						? "Ora insegni qui"
						: "Non insegni più qui"
					: teaches
						? `${name(colleague)} ora insegna qui`
						: `${name(colleague)} non insegna più qui`,
				{ position: "top-center" },
			)
		} catch (err) {
			toast.error(err.message || "Non sono riuscito a cambiarlo", { position: "top-center" })
		} finally {
			setChangingID(null)
		}
	}

	// Un ruolo cambia le lezioni di tutti e i poteri sul circolo; quelle dei
	// singoli colleghi restano come sono
	const applyRole = (colleague, role) =>
		save(
			colleague,
			{ ...accessHere(colleague), ...ROLE_PRESETS[role] },
			`${name(colleague)} ora è ${ROLE_LABELS[role].toLowerCase()}`,
		)

	const setAllLessons = (colleague, allLessons) =>
		save(
			colleague,
			{ ...accessHere(colleague), allLessons },
			allLessons === "edit"
				? `${name(colleague)} ora modifica le lezioni di tutti`
				: allLessons === "view"
					? `${name(colleague)} ora vede le lezioni di tutti`
					: `${name(colleague)} non guarda più le lezioni di tutti`,
		)

	const setCoachLevel = (colleague, other, level) => {
		const access = accessHere(colleague)
		save(
			colleague,
			{ ...access, coaches: { ...access.coaches, [other.coachID]: level } },
			level === "edit"
				? `${name(colleague)} ora modifica le lezioni di ${name(other)}`
				: level === "view"
					? `${name(colleague)} ora vede le lezioni di ${name(other)}`
					: `${name(colleague)} non guarda più le lezioni di ${name(other)}`,
		)
	}

	const togglePower = (colleague, power, label) => {
		const access = accessHere(colleague)
		const isOn = !access[power]
		save(
			colleague,
			{ ...access, [power]: isOn },
			`${name(colleague)} ${isOn ? "ora" : "non"} ${label}`,
		)
	}

	// Passare il circolo è una proposta: cambia qualcosa solo quando l'altro
	// accetta, dal suo profilo. Vedi "Passare il circolo" in
	// docs/circoli.md.
	const offerSchool = async colleague => {
		setChangingID(colleague.coachID)
		try {
			const offer = callFunction("offerSchoolOwnership")
			await offer({ schoolID: selectedSchool, coachID: colleague.coachID, ownerBecomes })
			toast.success(`Proposta mandata: aspetta che ${firstNameOf(name(colleague))} accetti`, {
				position: "top-center",
			})
			startPassing(null)
		} catch (err) {
			toast.error(err.message || "Non sono riuscito a proporglielo", {
				position: "top-center",
			})
		} finally {
			setChangingID(null)
		}
	}

	const withdrawOffer = async colleague => {
		setChangingID(colleague.coachID)
		try {
			const withdraw = callFunction("withdrawSchoolOwnership")
			await withdraw({ schoolID: selectedSchool })
			toast.success("Proposta ritirata", { position: "top-center" })
		} catch (err) {
			toast.error(err.message || "Non sono riuscito a ritirarla", { position: "top-center" })
		} finally {
			setChangingID(null)
		}
	}

	const caption = (text, extra = {}) => (
		<Text textSize="caption" textColor={theme.colors.text_light} {...extra}>
			{text}
		</Text>
	)

	const chipRow = children => (
		<Div d="flex" m={{ t: "0.3rem" }} style={{ gap: "0.4rem", flexWrap: "wrap" }}>
			{children}
		</Div>
	)

	// Il sottotitolo di una persona: il ruolo, e se non insegna
	const subtitleOf = colleague => {
		const role = roleOf(colleague, school, selectedSchool)
		const teaches = teachesInSchool(colleague, selectedSchool)
		return `${ROLE_LABELS[role]}${teaches ? "" : " · non insegna"}`
	}

	const customize = colleague => {
		const access = accessHere(colleague)
		const others = coaches.filter(other => other.coachID !== colleague.coachID)

		return (
			<Div p={{ b: "0.875rem" }}>
				{caption("Le lezioni di tutti i maestri")}
				{chipRow(
					LESSON_LEVELS.map(({ level, text }) => (
						<ToggleChip
							key={level}
							text={text}
							isSelected={access.allLessons === level}
							color={theme.colors.primary}
							onClick={() =>
								changingID === null &&
								access.allLessons !== level &&
								setAllLessons(colleague, level)
							}
						/>
					)),
				)}

				{/* chi modifica le lezioni di tutti non ha bisogno di nomi */}
				{access.allLessons !== "edit" && others.length > 0 && (
					<Div m={{ t: "0.8rem" }}>
						{caption("Quelle di un collega, una per una")}
						{others.map(other => {
							const current =
								access.coaches[other.coachID] ??
								(access.allLessons === "view" ? "view" : "none")
							// chi vede già tutto sceglie solo se modificare
							const levels = LESSON_LEVELS.filter(
								({ level }) => access.allLessons === "none" || level !== "none",
							)
							return (
								<Div key={other.coachID} m={{ t: "0.5rem" }}>
									<Text textSize="body">{name(other)}</Text>
									{chipRow(
										levels.map(({ level, text }) => (
											<ToggleChip
												key={level}
												text={text}
												isSelected={current === level}
												onClick={() =>
													changingID === null &&
													current !== level &&
													setCoachLevel(colleague, other, level)
												}
											/>
										)),
									)}
								</Div>
							)
						})}
					</Div>
				)}

				<Div m={{ t: "0.8rem" }}>
					{caption("Sul circolo")}
					{chipRow(
						<>
							<ToggleChip
								text="Cambia campi, listino e dati"
								isSelected={access.editSchool}
								onClick={() =>
									changingID === null &&
									togglePower(
										colleague,
										"editSchool",
										"cambia i dati del circolo",
									)
								}
							/>
						</>,
					)}
					{caption(
						"Far entrare colleghi lo fa chi modifica le lezioni di tutti: i gestori, e tu.",
						{
							m: { t: "0.4rem" },
						},
					)}
				</Div>
			</Div>
		)
	}

	const closeButton = (
		<Div m={{ x: "1rem" }}>
			<Row m={{ t: "1rem" }}>
				<Col p="0">
					<SecondaryButton
						m="auto"
						textColor={theme.colors.gunmetal}
						textSize="subheader"
						text="Chiudi"
						action={closeModal}
					/>
				</Col>
			</Row>
		</Div>
	)

	const sectionTitle = text => (
		<Text textWeight="bold" textColor={theme.colors.gunmetal} m={{ t: "1.25rem", b: "0.4rem" }}>
			{text}
		</Text>
	)

	// ── PASSARE IL CIRCOLO: LA CONFERMA ─────────────────────────────────
	// Prima di proporlo si dice cosa si resta, e cosa non si potrà più fare:
	// è l'unico passo che toglie qualcosa al proprietario.
	const receiver = isOwner && coaches.find(colleague => colleague.coachID === passingTo)
	if (receiver) {
		const receiverName = firstNameOf(name(receiver))
		const isChanging = changingID === receiver.coachID
		const iTeach = teachesInSchool(coach, selectedSchool)

		return (
			<Container>
				<StepTransition stepKey={`pass-${receiver.coachID}`} direction={direction}>
					<ScrollArea>
						<Div textWeight="bold" textSize="title">
							Passi {school.schoolName ?? "il circolo"} a {receiverName}?
						</Div>
						{caption(
							`${receiverName} deve accettare: finché non lo fa non cambia niente, e puoi ritirare la proposta.`,
							{ m: { t: "0.25rem" } },
						)}

						{sectionTitle("Tu resterai")}
						<RadioBlock
							options={OWNER_BECOMES}
							selected={ownerBecomes}
							onSelect={setOwnerBecomes}
						/>
						{caption(
							iTeach
								? "Continui a insegnare come adesso."
								: "Come adesso, senza insegnare.",
							{ m: { t: "0.5rem" } },
						)}

						<Block m={{ t: "1.25rem" }} p="0.875rem">
							<Text textSize="body" textWeight="bold">
								Non potrai più
							</Text>
							{/* da collega, anche quello che fa un gestore */}
							{[
								"decidere chi fa cosa nel circolo",
								"togliere colleghi",
								"chiudere il circolo",
								...(ownerBecomes === "colleague"
									? [
											"modificare le lezioni di tutti",
											"far entrare colleghi",
											"cambiare campi, listino e dati",
										]
									: []),
							].map(line => (
								<Text key={line} textSize="body" textColor={theme.colors.gunmetal}>
									· {line}
								</Text>
							))}
							{caption("Le lezioni, gli allievi e i conti restano dove sono.", {
								m: { t: "0.4rem" },
							})}
						</Block>
					</ScrollArea>
				</StepTransition>

				<Div m={{ x: "1rem" }}>
					<Row m={{ t: "1.5rem" }}>
						<Col p="0">
							<SecondaryButton
								m="auto"
								textColor={theme.colors.gunmetal}
								textSize="subheader"
								text={isChanging ? "Un attimo..." : "Indietro"}
								disabled={isChanging}
								action={() => startPassing(null)}
							/>
						</Col>
						{!isChanging && (
							<Col p="0">
								<PrimaryButton
									m="auto"
									bg={theme.colors.primary}
									textColor="white"
									textSize="subheader"
									text={`Proponi a ${receiverName}`}
									action={() => offerSchool(receiver)}
								/>
							</Col>
						)}
					</Row>
				</Div>
			</Container>
		)
	}

	// ── LA SCHEDA DELLA PERSONA ─────────────────────────────────────────
	const opened = coaches.find(colleague => colleague.coachID === openID)
	if (opened) {
		const me = isMe(opened)
		const role = roleOf(opened, school, selectedSchool)
		const teaches = teachesInSchool(opened, selectedSchool)
		const isChanging = changingID === opened.coachID
		// i ruoli si scelgono per gli altri: il proprietario ha tutto comunque
		const canChangeRole = isOwner && !me && role !== "owner"
		// insegnare o no il proprietario lo decide per tutti, anche per sé
		const canChangeTeaching = isOwner
		// il circolo si passa a chi c'è già, una proposta alla volta
		const offer = school.ownershipOffer
		const isOfferedHere = isOwner && offer?.to === opened.coachID
		const canPass = isOwner && !me && !isOfferedHere
		const offeredElsewhere =
			offer && offer.to !== opened.coachID
				? coaches.find(colleague => colleague.coachID === offer.to)
				: null

		const teachingHint = me
			? teaches
				? "Se lo spegni resti proprietario e decidi tutto come prima, ma non sei più tra i maestri: l'app si apre sulle lezioni dei colleghi."
				: "Spento: non hai una giornata tua e non ti si sceglie per una lezione. Le lezioni che hai già fatto restano tue."
			: teaches
				? "Ha una giornata sua e si sceglie per le lezioni, come maestro o assistente."
				: "Spento: non ha una giornata sua e non si sceglie per una lezione. Le lezioni che ha già fatto restano sue."

		return (
			<Container>
				<StepTransition stepKey={opened.coachID} direction={direction}>
					<RowButton label="Torna a chi c'è nel circolo" onClick={() => openPerson(null)}>
						<FontAwesomeIcon
							icon={faChevronLeft}
							color={theme.colors.primary}
							size="sm"
						/>
						<Text textSize="body" textColor={theme.colors.primary}>
							Chi c'è nel circolo
						</Text>
					</RowButton>

					<ScrollArea>
						<Div
							d="flex"
							align="center"
							m={{ t: "0.5rem" }}
							style={{ gap: "0.875rem" }}>
							<CoachAvatar coach={opened} muted={!teaches} size={56} />
							<Div>
								<Text textWeight="bold" textSize="title">
									{name(opened)}
									{me ? " (tu)" : ""}
								</Text>
								{caption(isChanging ? "Un attimo..." : subtitleOf(opened))}
							</Div>
						</Div>

						{isOfferedHere && (
							<Block
								m={{ t: "1.25rem" }}
								p="0.875rem"
								bg={theme.colors.primary_light}
								d="flex"
								style={{ gap: "0.75rem" }}>
								<FontAwesomeIcon
									icon={faClock}
									color={theme.colors.primary}
									style={{ marginTop: "0.2rem" }}
								/>
								<Div>
									<Text textSize="body" textWeight="bold">
										Gli hai proposto il circolo
									</Text>
									{caption(
										`Aspetta che ${firstNameOf(name(opened))} accetti. Poi tu resterai ${ROLE_LABELS[
											offer.ownerBecomes
										].toLowerCase()}.`,
									)}
									<RowButton
										style={{ padding: "0.5rem 0 0" }}
										onClick={() =>
											changingID === null && withdrawOffer(opened)
										}>
										<Text
											textSize="body"
											textWeight="bold"
											textColor={theme.colors.primary}>
											Ritira la proposta
										</Text>
									</RowButton>
								</Div>
							</Block>
						)}

						{canChangeTeaching && (
							<Block
								m={{ t: "1.25rem" }}
								p="0.875rem"
								d="flex"
								align="center"
								justify="space-between"
								cursor="pointer"
								style={{ gap: "0.75rem" }}
								onClick={() =>
									changingID === null && setTeaching(opened, !teaches)
								}>
								<Div>
									<Text textSize="body">
										{me
											? "Insegni in questo circolo"
											: "Insegna in questo circolo"}
									</Text>
									{caption(teachingHint)}
								</Div>
								<Switch
									m="0"
									shadow="0"
									checked={teaches}
									isLoading={isChanging}
									activeColor={theme.colors.gunmetal_fill}
									inactiveColor="gray_light"
									role="switch"
									aria-checked={teaches}
									aria-label={
										me
											? "Insegni in questo circolo"
											: "Insegna in questo circolo"
									}
									style={{ flexShrink: 0 }}
								/>
							</Block>
						)}

						{sectionTitle(me ? "Cosa puoi fare" : "Cosa può fare")}

						{role === "owner" ? (
							<Block p="0.875rem">
								<Text textSize="body">
									{me ? "Tutto: il circolo è tuo" : "Tutto: il circolo è suo"}
								</Text>
								{caption(
									me
										? "Modifichi e assegni le lezioni di tutti, fai entrare colleghi, cambi campi, listino e dati, e decidi cosa fanno gli altri."
										: "Modifica e assegna le lezioni di tutti, fa entrare colleghi, cambia campi, listino e dati, e decide cosa fanno gli altri.",
								)}
							</Block>
						) : canChangeRole ? (
							<Block>
								{Object.keys(ROLE_PRESETS).map(preset => (
									<React.Fragment key={preset}>
										<RadioRow
											title={ROLE_LABELS[preset]}
											hint={ROLE_HINTS[preset]}
											isSelected={role === preset}
											onSelect={() =>
												changingID === null &&
												role !== preset &&
												applyRole(opened, preset)
											}
										/>
										<Divider inset="2rem" />
									</React.Fragment>
								))}
								<RowButton
									aria-expanded={isCustomizing || role === "custom"}
									onClick={() =>
										setIsCustomizing(!(isCustomizing || role === "custom"))
									}>
									<Div
										w="20px"
										d="flex"
										justify="center"
										aria-hidden="true"
										style={{ flexShrink: 0 }}>
										<FontAwesomeIcon
											icon={faSliders}
											color={theme.colors.gunmetal_light}
										/>
									</Div>
									<Div flexGrow="1">
										<Text
											textSize="body"
											textWeight={role === "custom" ? "bold" : "normal"}>
											Personalizza
										</Text>
										{caption(
											role === "custom"
												? describe(opened).join(" · ")
												: "Le lezioni di un collega una per una, o un potere solo",
										)}
									</Div>
									<FontAwesomeIcon
										icon={
											isCustomizing || role === "custom"
												? faChevronUp
												: faChevronDown
										}
										color={theme.colors.gunmetal_light}
										size="sm"
									/>
								</RowButton>
								{(isCustomizing || role === "custom") && customize(opened)}
							</Block>
						) : (
							<Block p="0.875rem">
								{describe(opened).map(line => (
									<Text key={line} textSize="body">
										{line}
									</Text>
								))}
								{caption(
									"Cosa può fare in più ognuno lo decide il proprietario del circolo.",
									{
										m: { t: "0.4rem" },
									},
								)}
							</Block>
						)}

						{me &&
							role === "owner" &&
							caption(
								"Per passare il circolo a qualcuno, apri la sua scheda: «Passagli il circolo».",
								{ m: { t: "0.6rem" } },
							)}

						{canPass && (
							<Block m={{ t: "1rem" }}>
								<RowButton
									onClick={() => {
										setOwnerBecomes("manager")
										startPassing(opened.coachID)
									}}>
									<Div
										aria-hidden="true"
										w="40px"
										h="40px"
										rounded="circle"
										bg={theme.colors.gunmetal_lighter}
										d="flex"
										align="center"
										justify="center"
										style={{ flexShrink: 0 }}>
										<FontAwesomeIcon
											icon={faKey}
											color={theme.colors.gunmetal}
										/>
									</Div>
									<Div flexGrow="1">
										<Text textSize="body" textWeight="bold">
											Passagli il circolo
										</Text>
										{caption(
											offeredElsewhere
												? `Diventa proprietario se accetta. La proposta a ${firstNameOf(name(offeredElsewhere))} si ritira.`
												: "Diventa proprietario se accetta. Tu resti gestore o collega.",
										)}
									</Div>
									<FontAwesomeIcon
										icon={faChevronRight}
										color={theme.colors.gunmetal_light}
										size="sm"
									/>
								</RowButton>
							</Block>
						)}

						{canChangeRole && (
							<Div m={{ t: "1.5rem" }}>
								<WideButton
									icon={faUserMinus}
									text="Togli dal circolo"
									bg={theme.colors.red_light}
									color={theme.colors.red}
									onClick={() => Modal.open("revokeColleague", opened)}
								/>
								{caption(
									"Non vedrà più allievi e lezioni di qui. Le lezioni che ha fatto restano.",
									{ textAlign: "center", m: { t: "0.5rem" } },
								)}
							</Div>
						)}
					</ScrollArea>
				</StepTransition>

				{closeButton}
			</Container>
		)
	}

	// ── L'ELENCO ────────────────────────────────────────────────────────
	// il proprietario per primo, poi per nome
	const sorted = [...coaches].sort((a, b) => {
		const aOwns = ownsSchool(a, school) ? 0 : 1
		const bOwns = ownsSchool(b, school) ? 0 : 1
		return aOwns - bOwns || name(a).localeCompare(name(b))
	})

	return (
		<Container>
			<StepTransition stepKey="list" direction={direction}>
				<ScrollArea>
					<HelpTip tip="ruoli" inline m={{ b: "0.25rem" }}>
						<Div textWeight="bold" textSize="title">
							Chi c'è nel circolo
						</Div>
					</HelpTip>
					{caption(
						isOwner
							? "Chi entra vede tutti gli allievi e tutte le lezioni del circolo. Tocca un nome per scegliere cosa può fare."
							: "Chi entra vede tutti gli allievi e tutte le lezioni del circolo. Tocca un nome per vedere cosa può fare: lo decide il proprietario.",
					)}

					{GROUPS.map(group => {
						const members = sorted.filter(colleague =>
							group.roles.includes(roleOf(colleague, school, selectedSchool)),
						)
						if (members.length === 0) return null

						return (
							<Div key={group.title}>
								<Div m={{ t: "1.25rem", b: "0.4rem" }}>
									<Text textWeight="bold" textColor={theme.colors.gunmetal}>
										{group.title}
									</Text>
									{caption(group.hint)}
								</Div>
								<Block>
									{members.map((colleague, i) => {
										const teaches = teachesInSchool(colleague, selectedSchool)
										return (
											<React.Fragment key={colleague.coachID}>
												{i > 0 && <Divider />}
												<RowButton
													onClick={() => {
														setIsCustomizing(false)
														openPerson(colleague.coachID)
													}}>
													<CoachAvatar
														coach={colleague}
														muted={!teaches}
													/>
													<Div flexGrow="1" style={{ minWidth: 0 }}>
														<Div
															d="flex"
															align="center"
															flexWrap="wrap"
															style={{ gap: "0.5rem" }}>
															<Text
																textSize="body"
																textWeight={
																	isMe(colleague)
																		? "bold"
																		: "normal"
																}>
																{name(colleague)}
																{isMe(colleague) ? " (tu)" : ""}
															</Text>
															{!teaches && (
																<Text
																	tag="span"
																	textSize="caption"
																	p="0.05rem 0.5rem"
																	rounded="10px"
																	bg={theme.colors.gray_light}
																	textColor={
																		theme.colors.text_light
																	}>
																	non insegna
																</Text>
															)}
														</Div>
														{caption(
															changingID === colleague.coachID
																? "Un attimo..."
																: ROLE_LABELS[
																		roleOf(
																			colleague,
																			school,
																			selectedSchool,
																		)
																	],
														)}
													</Div>
													<FontAwesomeIcon
														icon={faChevronRight}
														color={theme.colors.gunmetal_light}
														size="sm"
													/>
												</RowButton>
											</React.Fragment>
										)
									})}
								</Block>
							</Div>
						)
					})}
				</ScrollArea>

				{canInvite && (
					<Div m={{ t: "1.5rem" }}>
						<WideButton
							icon={faUserPlus}
							text="Fai entrare un collega"
							bg={theme.colors.primary}
							color={theme.colors.white}
							shadow={theme.shadows.accent}
							onClick={() => Modal.open("schoolInvite")}
						/>
					</Div>
				)}
			</StepTransition>

			{closeButton}
		</Container>
	)
}

export default SchoolTeamModal
